import React from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useNavigation } from '../../hooks/useNavigation'
import { useOrientation } from '../../hooks/useOrientation'
import { usePendingOrdersCount } from '../../hooks/usePendingOrdersCount'
import { HamburgerButton } from './HamburgerButton'
import { SideNavigation } from './SideNavigation'

interface AdminLayoutProps {
  children: React.ReactNode
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
  const orientation = useOrientation()
  const { count: pendingOrdersCount, isError } = usePendingOrdersCount()
  const { isMobileMenuOpen, toggleMobileMenu, isExpanded } = useNavigation()

  // Show hamburger on mobile or tablet portrait
  const shouldShowHamburger = isMobile || (isTablet && orientation === 'portrait')

  // Calculate navigation width for desktop layout — matches SideNavigation's collapse logic
  const navWidth = shouldShowHamburger ? 0 : isExpanded ? 280 : 64

  // Format user data
  const userData = user ? {
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'Admin User',
    email: user.email || 'admin@munchies.com',
    avatar: user.user_metadata?.avatar_url,
    role: user.user_metadata?.role || 'Administrator'
  } : null

  return (
    <div className="min-h-screen bg-white/80 backdrop-blur-xl">
      {/* Hamburger Button */}
      {shouldShowHamburger && (
        <HamburgerButton onClick={toggleMobileMenu} isOpen={isMobileMenuOpen} />
      )}

      {/* Side Navigation */}
      <SideNavigation
        user={userData}
        onLogout={signOut}
        pendingOrdersCount={isError ? undefined : pendingOrdersCount}
      />

      {/* Main Content */}
      <main
        id="main-content"
        className="transition-all duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          marginLeft: shouldShowHamburger ? '0' : `${navWidth}px`,
          paddingLeft: shouldShowHamburger ? '16px' : '0',
          paddingRight: shouldShowHamburger ? '16px' : '0',
          paddingTop: '0',
          paddingBottom: '0'
        }}
      >
        {children}
      </main>
    </div>
  )
}
